import React, { createContext, useContext, useEffect, useState } from 'react';
import { generateId } from '../utils/helpers';
import { supabase } from '../supabase/supabase';
import { useTenant } from './TenantContext';
import {
  searchEnheter,
  normalizeEntity,
  mapEntityToCompany,
  getCanonicalOrgNumber,
  createBrregError
} from '../services/BrregService';

const BrregContext = createContext();

const isValidUuid = value => (
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
);

const appendAdvancedBrregFilters = (url, filters = {}) => {
  const advancedFilters = [
    'navn',
    'fraAntallAnsatte',
    'tilAntallAnsatte',
    'fraStiftelsesdato',
    'tilStiftelsesdato',
    'sort'
  ];

  const params = advancedFilters
    .filter(field => filters[field] !== undefined && filters[field] !== null && filters[field] !== '')
    .map(field => `${field}=${encodeURIComponent(filters[field])}`);

  const booleanFilters = [
    'registrertIMvaregisteret',
    'registrertIForetaksregisteret'
  ];

  booleanFilters.forEach(field => {
    if (filters[field] !== undefined && filters[field] !== null && filters[field] !== '') {
      params.push(`${field}=${encodeURIComponent(filters[field])}`);
    }
  });

  return params.length > 0 ? `${url}&${params.join('&')}` : url;
};

const getSearchError = (result, fallbackMessage) => {
  if (!result?.error) return null;

  const brregError = createBrregError(
    {
      status: result.status,
      message: result.error
    },
    fallbackMessage
  );

  return new Error(brregError.message || fallbackMessage);
};

const normalizeOrgNumberForDuplicateCheck = value => (
  String(value ?? '').replace(/\D/g, '')
);

const normalizeCompanyNameForDuplicateCheck = value => (
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
);

const getLeadName = lead => (
  lead?.name ||
  lead?.companyName ||
  lead?.company_name ||
  ''
);

const getLeadOrgNumber = lead => (
  lead?.orgnr ||
  lead?.org_number ||
  lead?.orgNumber ||
  lead?.organization_number ||
  lead?.organizationNumber ||
  lead?.organisasjonsnummer ||
  ''
);

const loadCrmDuplicateIndex = async tenantId => {
  try {
    const { data, error: companiesError } = await supabase
      .from('companies_1710925200000')
      .select('org_number,name')
      .eq('tenant_id', tenantId);

    if (companiesError) {
      throw companiesError;
    }

    const orgNumbers = new Set();
    const names = new Set();

    (Array.isArray(data) ? data : []).forEach(company => {
      const orgNumber = normalizeOrgNumberForDuplicateCheck(company.org_number);
      const name = normalizeCompanyNameForDuplicateCheck(company.name);

      if (orgNumber) {
        orgNumbers.add(orgNumber);
      }

      if (name) {
        names.add(name);
      }
    });

    return { orgNumbers, names };
  } catch (duplicateError) {
    console.warn(
      '[BRREG] CRM-duplikatsjekk kunne ikke fullføres. Importen fortsetter uten CRM-markering.',
      duplicateError
    );
    return null;
  }
};

const getCrmDuplicateFields = (lead, duplicateIndex) => {
  if (!duplicateIndex) {
    return {
      isDuplicate: false,
      duplicateReason: ''
    };
  }

  const orgNumber = normalizeOrgNumberForDuplicateCheck(getLeadOrgNumber(lead));
  const name = normalizeCompanyNameForDuplicateCheck(getLeadName(lead));
  const isDuplicate = orgNumber
    ? duplicateIndex.orgNumbers.has(orgNumber)
    : Boolean(name && duplicateIndex.names.has(name));

  return {
    isDuplicate,
    duplicateReason: isDuplicate ? 'Finnes allerede i CRM' : ''
  };
};

const isCrmDuplicateLead = lead => (
  Boolean(
    lead?.crmDuplicate ||
    lead?.crm_duplicate ||
    lead?.isCrmDuplicate ||
    lead?.duplicateReason === 'Finnes allerede i CRM' ||
    lead?.duplicate_reason === 'Finnes allerede i CRM' ||
    lead?.block_reason === 'Finnes allerede i CRM'
  )
);

export const useBrreg = () => {
  const context = useContext(BrregContext);

  if (!context) {
    throw new Error('useBrreg must be used within a BrregProvider');
  }

  return context;
};

export const BrregProvider = ({ children }) => {
  const { activeTenantId } = useTenant();
  const [importJobs, setImportJobs] = useState([]);
  const [stagingLeads, setStagingLeads] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadBrregData = async () => {
      if (!activeTenantId) {
        setImportJobs([]);
        setStagingLeads([]);
        setError('Mangler aktiv tenant');
        return;
      }

      const { data: jobs } = await supabase
        .from('brreg_import_jobs')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .order('created_at', { ascending: false });

      const { data: brregSettings } = await supabase
        .from('brreg_settings')
        .select('*')
        .eq('connector_id', 'brreg_default')
        .maybeSingle();

      if (jobs) setImportJobs(jobs);
      if (brregSettings) setSettings(brregSettings);
      setError('');
    };

    loadBrregData();
  }, [activeTenantId]);

  const runImport = async queryConfig => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return null;
    }

    setIsImporting(true);

    try {
      let url = 'https://data.brreg.no/enhetsregisteret/api/enheter?size=50';

      if (queryConfig.naeringskode) {
        url += `&naeringskode=${queryConfig.naeringskode}`;
      }

      if (queryConfig.kommune) {
        url += `&kommunenummer=${queryConfig.kommune}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      const enheter = data._embedded?.enheter || [];
      const newJobId = generateId('job');

      const newLeads = enheter.map(enhet => {
        const canonicalOrgNumber = getCanonicalOrgNumber(enhet);

        return {
          id: generateId('stg'),
          job_id: newJobId,
          tenant_id: activeTenantId,
          orgnr: canonicalOrgNumber,
          name: enhet.navn,
          orgform: enhet.organisasjonsform?.beskrivelse || '',
          naeringskode: enhet.naeringskode1?.kode || '',
          naeringskode_desc: enhet.naeringskode1?.beskrivelse || '',
          city: enhet.forretningsadresse?.poststed || '',
          website: enhet.hjemmeside || '',
          wash_status: 'Ny',
          data_quality_score: Math.floor(Math.random() * 40) + 60
        };
      });

      const newJob = {
        job_id: newJobId,
        tenant_id: activeTenantId,
        segment_name: queryConfig.segment_name || 'Hurtig-import',
        status: 'Klargjort',
        created_at: new Date().toISOString(),
        result_summary: {
          staged: newLeads.length,
          errors: 0,
          exported: 0
        }
      };

      setImportJobs(prev => [newJob, ...prev]);
      setStagingLeads(prev => [...newLeads, ...prev]);
    } catch (error) {
      console.error('Feil ved henting fra BRREG:', error);
      alert('Kunne ikke hente data fra Brønnøysund. Sjekk at næringskoden er gyldig.');
    } finally {
      setIsImporting(false);
    }
  };

  const loadStagingLeads = async jobId => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return [];
    }

    if (!jobId) return [];

    const { data, error: fetchError } = await supabase
      .from('brreg_staging_leads')
      .select('*')
      .eq('job_id', jobId)
      .eq('tenant_id', activeTenantId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      console.error('Kunne ikke laste BRREG staging-leads:', fetchError);
      return [];
    }

    setStagingLeads(data || []);
    return data || [];
  };

  const previewImport = async filters => {
    const result = await searchEnheter(filters, 0, 1);
    const searchError = getSearchError(
      result,
      'Kunne ikke forhåndsvise BRREG-søket.'
    );

    if (searchError) throw searchError;

    return result.total || 0;
  };

  const runImportJob = async (segmentName, filters) => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return null;
    }

    setIsImporting(true);

    try {
      const result = await searchEnheter(filters, 0, filters.max_rows_target || 50);
      const searchError = getSearchError(
        result,
        'Kunne ikke hente data fra Brønnøysundregistrene.'
      );

      if (searchError) throw searchError;

      const enheter = result.data || [];
      const userId = null;
      const duplicateIndex = await loadCrmDuplicateIndex(activeTenantId);

      const { data: job, error: jobError } = await supabase
        .from('brreg_import_jobs')
        .insert([{
          tenant_id: activeTenantId,
          created_by: userId,
          segment_name: segmentName,
          status: 'Klargjort',
          mode: 'Manuell',
          query_config: filters,
          result_summary: {
            staged: enheter.length,
            new_leads: enheter.length,
            errors: 0,
            exported: 0
          }
        }])
        .select()
        .single();

      if (jobError) throw jobError;

      const leads = enheter.map(enhet => {
        const normalized = normalizeEntity(enhet);
        const canonicalOrgNumber = getCanonicalOrgNumber(normalized);
        mapEntityToCompany(normalized);
        const duplicateFields = getCrmDuplicateFields({
          orgnr: canonicalOrgNumber,
          name: enhet.navn
        }, duplicateIndex);

        return {
          tenant_id: activeTenantId,
          job_id: job.job_id,
          orgnr: canonicalOrgNumber,
          name: enhet.navn,
          orgform: enhet.organisasjonsform?.beskrivelse || '',
          status_brreg: enhet.status || '',
          naeringskode: enhet.naeringskode1?.kode || '',
          naeringskode_desc: enhet.naeringskode1?.beskrivelse || '',
          address: enhet.forretningsadresse?.adresse?.join(', ') || '',
          postnr: enhet.forretningsadresse?.postnummer || '',
          poststed: enhet.forretningsadresse?.poststed || '',
          kommune: enhet.forretningsadresse?.kommune || '',
          website: enhet.hjemmeside || '',
          phone: enhet.telefon || '',
          email: enhet.epostadresse || '',
          data_quality_score: enhet.hjemmeside ? 80 : 60,
          wash_status: duplicateFields.isDuplicate ? 'Duplikat' : 'Ny',
          block_reason: duplicateFields.duplicateReason,
          roles_enriched: false,
          signatur_checked: false
        };
      });

      if (leads.length > 0) {
        const { error: leadsError } = await supabase
          .from('brreg_staging_leads')
          .insert(leads);

        if (leadsError) throw leadsError;
      }

      setImportJobs(prev => [job, ...prev]);
      setStagingLeads(leads);

      return job.job_id;
    } catch (error) {
      console.error('Feil ved BRREG-importjobb:', error);
      alert(error.message || 'Kunne ikke kjøre BRREG-importen.');
      return null;
    } finally {
      setIsImporting(false);
    }
  };

  const refreshJob = async jobId => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return null;
    }

    const existingJob = importJobs.find(job => job.job_id === jobId);

    if (!existingJob || existingJob.tenant_id !== activeTenantId) return null;

    setIsImporting(true);

    try {
      const filters = existingJob.query_config || {};
      const result = await searchEnheter(filters, 0, filters.max_rows_target || 50);
      const searchError = getSearchError(
        result,
        'Kunne ikke oppdatere BRREG-jobben.'
      );

      if (searchError) throw searchError;

      const enheter = result.data || [];
      const duplicateIndex = await loadCrmDuplicateIndex(activeTenantId);
      const existingLeads = await loadStagingLeads(jobId);
      const existingOrgNumbers = new Set(
        existingLeads.map(lead => getCanonicalOrgNumber(lead))
      );
      const newEntities = enheter.filter(entity => (
        !existingOrgNumbers.has(getCanonicalOrgNumber(entity))
      ));

      const newLeads = newEntities.map(enhet => {
        const normalized = normalizeEntity(enhet);
        const canonicalOrgNumber = getCanonicalOrgNumber(normalized);
        mapEntityToCompany(normalized);
        const duplicateFields = getCrmDuplicateFields({
          orgnr: canonicalOrgNumber,
          name: enhet.navn
        }, duplicateIndex);

        return {
          tenant_id: activeTenantId,
          job_id: jobId,
          orgnr: canonicalOrgNumber,
          name: enhet.navn,
          orgform: enhet.organisasjonsform?.beskrivelse || '',
          status_brreg: enhet.status || '',
          naeringskode: enhet.naeringskode1?.kode || '',
          naeringskode_desc: enhet.naeringskode1?.beskrivelse || '',
          address: enhet.forretningsadresse?.adresse?.join(', ') || '',
          postnr: enhet.forretningsadresse?.postnummer || '',
          poststed: enhet.forretningsadresse?.poststed || '',
          kommune: enhet.forretningsadresse?.kommune || '',
          fylke: enhet.forretningsadresse?.fylke || '',
          website: enhet.hjemmeside || '',
          phone: enhet.telefon || '',
          email: enhet.epostadresse || '',
          data_quality_score: enhet.hjemmeside ? 80 : 60,
          wash_status: duplicateFields.isDuplicate ? 'Duplikat' : 'Ny',
          block_reason: duplicateFields.duplicateReason,
          roles_enriched: false,
          signatur_checked: false
        };
      });

      if (newLeads.length > 0) {
        const { error: leadsError } = await supabase
          .from('brreg_staging_leads')
          .insert(newLeads);

        if (leadsError) throw leadsError;
      }

      const resultSummary = {
        ...(existingJob.result_summary || {}),
        staged: existingLeads.length + newLeads.length,
        new_leads: newLeads.length,
        last_refresh_new: newLeads.length
      };

      const { error: updateError } = await supabase
        .from('brreg_import_jobs')
        .update({
          result_summary: resultSummary,
          refresh_count: (existingJob.refresh_count || 0) + 1,
          last_refreshed_at: new Date().toISOString()
        })
        .eq('job_id', jobId)
        .eq('tenant_id', activeTenantId);

      if (updateError) throw updateError;

      const updatedJobs = await supabase
        .from('brreg_import_jobs')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .order('created_at', { ascending: false });

      if (updatedJobs.data) setImportJobs(updatedJobs.data);
      await loadStagingLeads(jobId);

      return {
        jobId,
        newLeads: newLeads.length,
        totalLeads: existingLeads.length + newLeads.length
      };
    } catch (error) {
      console.error('Feil ved oppdatering av BRREG-jobb:', error);
      alert(error.message || 'Kunne ikke oppdatere BRREG-jobben.');
      return null;
    } finally {
      setIsImporting(false);
    }
  };

  const updateLeadStatus = async (leadIds, washStatus) => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return false;
    }

    const ids = Array.isArray(leadIds) ? leadIds : [leadIds];

    if (ids.length === 0) return false;

    const { error: updateError } = await supabase
      .from('brreg_staging_leads')
      .update({ wash_status: washStatus })
      .in('id', ids)
      .eq('tenant_id', activeTenantId);

    if (updateError) {
      console.error('Kunne ikke oppdatere lead-status:', updateError);
      alert('Kunne ikke oppdatere status for leadet.');
      return false;
    }

    setStagingLeads(prev => prev.map(lead => (
      ids.includes(lead.id) ? { ...lead, wash_status: washStatus } : lead
    )));

    return true;
  };

  const persistWashStatuses = async statusChanges => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return;
    }

    for (const [status, ids] of Object.entries(statusChanges)) {
      if (ids.length === 0) continue;

      const { error: updateError } = await supabase
        .from('brreg_staging_leads')
        .update({ wash_status: status })
        .in('id', ids)
        .eq('tenant_id', activeTenantId);

      if (updateError) {
        console.error(`Kunne ikke lagre status "${status}":`, updateError);
        alert(`Vaskestatus ble oppdatert lokalt, men kunne ikke lagres for statusen "${status}".`);
      }
    }
  };

  const washLeads = async (jobId, config = {}) => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return [];
    }

    if (!jobId) {
      console.error('Kan ikke vaske BRREG-leads uten valgt importjobb.');
      alert('Velg en importjobb før du kjører vask.');
      return [];
    }

    const washConfig = {
      preventDuplicates: true,
      requireContactInfo: false,
      requireWebsite: false,
      minQualityScore: 30,
      ...config
    };

    const job = importJobs.find(item => item.job_id === jobId);
    const tenantJob = job?.tenant_id === activeTenantId ? job : null;
    const requireWebsite = washConfig.requireWebsite === true
      || tenantJob?.query_config?.has_website === true;
    const seenOrgNumbers = new Set();
    const statusChanges = {};
    const jobLeads = stagingLeads
      .filter(lead => lead.job_id === jobId)
      .filter(lead => lead.tenant_id === activeTenantId);

    const evaluatedLeads = jobLeads.map(lead => {
      const orgNumber = getCanonicalOrgNumber(lead);
      const crmDuplicate = isCrmDuplicateLead(lead);
      let washStatus = crmDuplicate ? 'Duplikat' : 'Godkjent';

      if (!crmDuplicate && washConfig.preventDuplicates && orgNumber) {
        if (seenOrgNumbers.has(orgNumber)) {
          washStatus = 'Duplikat';
        } else {
          seenOrgNumbers.add(orgNumber);
        }
      }

      if (washStatus === 'Godkjent' && washConfig.requireContactInfo && !lead.email && !lead.phone) {
        washStatus = 'Mangler kontaktinfo';
      }

      if (washStatus === 'Godkjent' && requireWebsite && !lead.website) {
        washStatus = 'Mangler nettside';
      }

      if (
        washStatus === 'Godkjent' &&
        Number(lead.data_quality_score || 0) < Number(washConfig.minQualityScore || 0)
      ) {
        washStatus = 'Under minimumsscore';
      }

      if (!statusChanges[washStatus]) statusChanges[washStatus] = [];
      if (lead.id) statusChanges[washStatus].push(lead.id);

      return {
        ...lead,
        org_number: orgNumber,
        wash_status: washStatus,
        block_reason: crmDuplicate
          ? 'Finnes allerede i CRM'
          : (lead.block_reason || '')
      };
    });

    setStagingLeads(prev => prev.map(lead => {
      const evaluated = evaluatedLeads.find(item => item.id === lead.id);
      return evaluated || lead;
    }));

    await persistWashStatuses(statusChanges);
    return evaluatedLeads;
  };

  const approveLeads = async minimumScore => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return false;
    }

    const blockedStatuses = new Set([
      'Duplikat',
      'Mangler kontaktinfo',
      'Mangler nettside',
      'Under minimumsscore',
      'Eksportert'
    ]);

    const leadIds = stagingLeads
      .filter(lead => (
        lead.wash_status === 'Vasket' &&
        !blockedStatuses.has(lead.wash_status) &&
        Number(lead.data_quality_score || 0) >= (minimumScore || 30) &&
        lead.id
      ))
      .filter(lead => lead.tenant_id === activeTenantId)
      .map(lead => lead.id);

    if (leadIds.length === 0) return false;

    return updateLeadStatus(leadIds, 'Godkjent');
  };

  const distributeLeads = async input => {
    if (!activeTenantId) {
      setError('Mangler aktiv tenant');
      return false;
    }

    const config = typeof input === 'string'
      ? {
        method: input.toLowerCase() === 'single' ? 'single' : 'round-robin',
        owners: []
      }
      : {
        ...input,
        method: input?.method === 'single' || input?.method === 'Single'
          ? 'single'
          : 'round-robin'
      };

    const overwrite = config.overwrite === true;
    const owners = Array.isArray(config.owners) && config.owners.length > 0
      ? config.owners.filter(owner => isValidUuid(owner?.id))
      : [];
    const selectedJobId = config.jobId || null;

    const blockedStatuses = new Set([
      'Duplikat',
      'Mangler kontaktinfo',
      'Mangler nettside',
      'Under minimumsscore',
      'Eksportert'
    ]);

    const eligibleLeads = stagingLeads
      .filter(lead => (
        (!selectedJobId || lead.job_id === selectedJobId) &&
        lead.wash_status === 'Godkjent' &&
        !blockedStatuses.has(lead.wash_status) &&
        lead.id &&
        (overwrite || !isValidUuid(lead.suggested_owner_id))
      ))
      .filter(lead => lead.tenant_id === activeTenantId);

    if (owners.length === 0) {
      const results = await Promise.all(
        eligibleLeads.map(async lead => {
          const { error: updateError } = await supabase
            .from('brreg_staging_leads')
            .update({ suggested_owner_id: null })
            .eq('id', lead.id)
            .eq('tenant_id', activeTenantId);

          return {
            id: lead.id,
            success: !updateError
          };
        })
      );

      const failedLeadIds = results
        .filter(result => !result.success)
        .map(result => result.id);

      const clearedLeadIds = results
        .filter(result => result.success)
        .map(result => result.id);

      if (clearedLeadIds.length > 0) {
        setStagingLeads(prev => prev.map(lead => (
          clearedLeadIds.includes(lead.id)
            ? { ...lead, suggested_owner_id: null }
            : lead
        )));
      }

      return {
        success: failedLeadIds.length === 0,
        assigned: 0,
        unassigned: eligibleLeads.length - failedLeadIds.length,
        message: failedLeadIds.length === 0
          ? 'Ingen gyldige eiere valgt – leads beholdes uten eier.'
          : 'Ingen gyldige eiere valgt – noen leads beholdes uten eier.',
        failedLeadIds
      };
    }

    if (
      config.method === 'single' &&
      !owners.some(owner => owner.id === config.ownerId)
    ) {
      alert('Velg en gyldig team-bruker før du fordeler leads.');
      return false;
    }

    if (eligibleLeads.length === 0) {
      alert('Fant ingen godkjente leads uten ansvarlig å fordele.');
      return false;
    }

    const assignments = eligibleLeads.map((lead, index) => {
      const owner = config.method === 'single'
        ? owners.find(item => item.id === config.ownerId)
        : owners[index % owners.length];

      return owner ? { lead, owner } : null;
    }).filter(Boolean);

    const failedLeadIds = [];
    const successfulAssignments = [];

    for (const assignment of assignments) {
      const { error: updateError } = await supabase
        .from('brreg_staging_leads')
        .update({ suggested_owner_id: assignment.owner.id })
        .eq('id', assignment.lead.id)
        .eq('tenant_id', activeTenantId);

      if (updateError) {
        failedLeadIds.push(assignment.lead.id);
      } else {
        successfulAssignments.push(assignment);
      }
    }

    if (successfulAssignments.length > 0) {
      const assignedById = new Map(
        successfulAssignments.map(({ lead, owner }) => [lead.id, owner.id])
      );

      setStagingLeads(prev => prev.map(lead => (
        assignedById.has(lead.id)
          ? { ...lead, suggested_owner_id: assignedById.get(lead.id) }
          : lead
      )));
    }

    if (failedLeadIds.length > 0) {
      alert(`${failedLeadIds.length} ansvarlig(e) kunne ikke lagres. Se konsollen for detaljer.`);
    }

    if (successfulAssignments.length > 0) {
      alert(`${successfulAssignments.length} godkjente leads ble fordelt.`);
    }

    return failedLeadIds.length === 0 && successfulAssignments.length > 0;
  };

  const exportLeadsToCRM = async () => ({
    success: false,
    deprecated: true,
    message: 'BRREG export is handled by ExportWizard via useCRMData.addCompany'
  });

  const value = {
    importJobs,
    stagingLeads,
    isImporting,
    settings,
    error,
    runImport,
    runImportJob,
    refreshJob,
    previewImport,
    loadStagingLeads,
    updateLeadStatus,
    exportLeadsToCRM,
    washLeads,
    approveLeads,
    distributeLeads
  };

  return (
    <BrregContext.Provider value={value}>
      {children}
    </BrregContext.Provider>
  );
};